/**
 * Frontend formula evaluator for COMPUTED form fields (Phase F, §2.4).
 *
 * Mirrors backend `com.kerkton.orna.service.form.FormulaEvaluator` (Groovy)
 * case-for-case. See `orna-spring/docs/house-check-request/evaluator-parity-cases.json`
 * for the shared parity corpus that both sides exercise.
 *
 * Grammar (whitelist):
 *   expr    := term   (('+' | '-') term)*
 *   term    := factor (('*' | '/') factor)*
 *   factor  := '-' factor | primary
 *   primary := NUMBER | IDENT | '(' expr ')'
 *
 * Identifiers: [a-zA-Z][a-zA-Z0-9_]*
 *
 * Resource limits (decision D7):
 *   - Formula length ≤ 500 chars
 *   - Parse recursion depth ≤ 20
 *   - Identifier count ≤ 50
 *
 * Safe degradation (decision E29):
 *   - `evaluateFormula` NEVER throws
 *   - divide-by-zero → 0
 *   - missing identifier → 0
 *   - any arithmetic / parse error → 0
 *
 * Numeric backing: native JS `number` (double precision).
 * The backend uses BigDecimal w/ MathContext.DECIMAL64 — the parity corpus
 * uses `expectedApprox` + `tolerance` for the one case where this matters
 * (non-terminating decimal division).
 */

export interface FormulaValidationResult {
  valid: boolean;
  errorCode?: string;
  errorMessage?: string;
  referencedKeys: string[];
}

export const MAX_FORMULA_LENGTH = 500;
export const MAX_PARSE_DEPTH = 20;
export const MAX_IDENTIFIER_COUNT = 50;

// ── Error codes (match backend strings) ────────────────────────────────
type FormulaErrorCode =
  | 'formula.syntax'
  | 'formula.too-long'
  | 'formula.depth-exceeded'
  | 'formula.too-many-identifiers';

class ParseError extends Error {
  code: FormulaErrorCode;
  constructor(code: FormulaErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

// ── Lexer helpers ──────────────────────────────────────────────────────

const isDigit = (c: string): boolean => c >= '0' && c <= '9';
const isWhitespace = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f' || c === '\v';
const isIdentStart = (c: string): boolean =>
  (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
const isIdentPart = (c: string): boolean =>
  isIdentStart(c) || isDigit(c) || c === '_';

// ── Parser (validate-only) ─────────────────────────────────────────────
// Walks the grammar and records referenced identifiers. No evaluation.

class Parser {
  src: string;
  pos = 0;
  referencedKeys: string[] = [];

  constructor(src: string) {
    this.src = src;
  }

  skipWhitespace(): void {
    while (this.pos < this.src.length && isWhitespace(this.src.charAt(this.pos))) {
      this.pos++;
    }
  }

  private checkDepth(depth: number): void {
    if (depth > MAX_PARSE_DEPTH) {
      throw new ParseError(
        'formula.depth-exceeded',
        `Formula parse depth exceeded ${MAX_PARSE_DEPTH}.`,
      );
    }
  }

  parseExpression(depth: number): void {
    this.checkDepth(depth);
    this.parseTerm(depth + 1);
    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.src.length) break;
      const c = this.src.charAt(this.pos);
      if (c === '+' || c === '-') {
        this.pos++;
        this.parseTerm(depth + 1);
      } else {
        break;
      }
    }
  }

  parseTerm(depth: number): void {
    this.checkDepth(depth);
    this.parseFactor(depth + 1);
    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.src.length) break;
      const c = this.src.charAt(this.pos);
      if (c === '*' || c === '/') {
        this.pos++;
        this.parseFactor(depth + 1);
      } else {
        break;
      }
    }
  }

  parseFactor(depth: number): void {
    this.checkDepth(depth);
    this.skipWhitespace();
    if (this.pos >= this.src.length) {
      throw new ParseError('formula.syntax', 'Unexpected end of formula.');
    }
    const c = this.src.charAt(this.pos);
    if (c === '-') {
      this.pos++;
      this.parseFactor(depth + 1);
      return;
    }
    this.parsePrimary(depth + 1);
  }

  parsePrimary(depth: number): void {
    this.checkDepth(depth);
    this.skipWhitespace();
    if (this.pos >= this.src.length) {
      throw new ParseError('formula.syntax', 'Unexpected end of formula.');
    }
    const c = this.src.charAt(this.pos);
    if (c === '(') {
      this.pos++;
      this.parseExpression(depth + 1);
      this.skipWhitespace();
      if (this.pos >= this.src.length || this.src.charAt(this.pos) !== ')') {
        throw new ParseError('formula.syntax', `Missing ')' at position ${this.pos}.`);
      }
      this.pos++;
      return;
    }
    if (isDigit(c) || c === '.') {
      this.parseNumber();
      return;
    }
    if (isIdentStart(c)) {
      const ident = this.readIdentifier();
      this.referencedKeys.push(ident);
      return;
    }
    throw new ParseError(
      'formula.syntax',
      `Unexpected character '${c}' at position ${this.pos}.`,
    );
  }

  private parseNumber(): void {
    const start = this.pos;
    let sawDot = false;
    while (this.pos < this.src.length) {
      const c = this.src.charAt(this.pos);
      if (isDigit(c)) {
        this.pos++;
      } else if (c === '.' && !sawDot) {
        sawDot = true;
        this.pos++;
      } else {
        break;
      }
    }
    if (start === this.pos) {
      throw new ParseError('formula.syntax', `Invalid number at position ${start}.`);
    }
    const tok = this.src.substring(start, this.pos);
    if (tok === '.') {
      throw new ParseError('formula.syntax', `Invalid number '.' at position ${start}.`);
    }
    // Validate parseability (mirrors backend `new BigDecimal(tok)`).
    const n = Number(tok);
    if (!Number.isFinite(n)) {
      throw new ParseError('formula.syntax', `Invalid number '${tok}' at position ${start}.`);
    }
  }

  private readIdentifier(): string {
    const start = this.pos;
    this.pos++;
    while (this.pos < this.src.length) {
      const c = this.src.charAt(this.pos);
      if (isIdentPart(c)) this.pos++;
      else break;
    }
    return this.src.substring(start, this.pos);
  }
}

// ── Evaluator (parse + compute) ────────────────────────────────────────

class Evaluator {
  src: string;
  values: Record<string, number>;
  pos = 0;
  identifierCount = 0;

  constructor(src: string, values: Record<string, number>) {
    this.src = src;
    this.values = values;
  }

  skipWhitespace(): void {
    while (this.pos < this.src.length && isWhitespace(this.src.charAt(this.pos))) {
      this.pos++;
    }
  }

  private checkDepth(depth: number): void {
    if (depth > MAX_PARSE_DEPTH) {
      throw new ParseError(
        'formula.depth-exceeded',
        `Formula parse depth exceeded ${MAX_PARSE_DEPTH}.`,
      );
    }
  }

  parseExpression(depth: number): number {
    this.checkDepth(depth);
    let left = this.parseTerm(depth + 1);
    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.src.length) break;
      const c = this.src.charAt(this.pos);
      if (c === '+') {
        this.pos++;
        left = left + this.parseTerm(depth + 1);
      } else if (c === '-') {
        this.pos++;
        left = left - this.parseTerm(depth + 1);
      } else {
        break;
      }
    }
    return left;
  }

  parseTerm(depth: number): number {
    this.checkDepth(depth);
    let left = this.parseFactor(depth + 1);
    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.src.length) break;
      const c = this.src.charAt(this.pos);
      if (c === '*') {
        this.pos++;
        left = left * this.parseFactor(depth + 1);
      } else if (c === '/') {
        this.pos++;
        const right = this.parseFactor(depth + 1);
        if (right === 0) {
          // Safe-degradation: divide-by-zero → 0.
          left = 0;
        } else {
          const q = left / right;
          left = Number.isFinite(q) ? q : 0;
        }
      } else {
        break;
      }
    }
    return left;
  }

  parseFactor(depth: number): number {
    this.checkDepth(depth);
    this.skipWhitespace();
    if (this.pos >= this.src.length) {
      throw new ParseError('formula.syntax', 'Unexpected end of formula.');
    }
    const c = this.src.charAt(this.pos);
    if (c === '-') {
      this.pos++;
      const v = this.parseFactor(depth + 1);
      return -v;
    }
    return this.parsePrimary(depth + 1);
  }

  parsePrimary(depth: number): number {
    this.checkDepth(depth);
    this.skipWhitespace();
    if (this.pos >= this.src.length) {
      throw new ParseError('formula.syntax', 'Unexpected end of formula.');
    }
    const c = this.src.charAt(this.pos);
    if (c === '(') {
      this.pos++;
      const inner = this.parseExpression(depth + 1);
      this.skipWhitespace();
      if (this.pos >= this.src.length || this.src.charAt(this.pos) !== ')') {
        throw new ParseError('formula.syntax', `Missing ')' at position ${this.pos}.`);
      }
      this.pos++;
      return inner;
    }
    if (isDigit(c) || c === '.') {
      return this.parseNumber();
    }
    if (isIdentStart(c)) {
      const ident = this.readIdentifier();
      this.identifierCount++;
      const v = this.values[ident];
      return v == null || !Number.isFinite(v) ? 0 : v;
    }
    throw new ParseError(
      'formula.syntax',
      `Unexpected character '${c}' at position ${this.pos}.`,
    );
  }

  private parseNumber(): number {
    const start = this.pos;
    let sawDot = false;
    while (this.pos < this.src.length) {
      const c = this.src.charAt(this.pos);
      if (isDigit(c)) {
        this.pos++;
      } else if (c === '.' && !sawDot) {
        sawDot = true;
        this.pos++;
      } else {
        break;
      }
    }
    const tok = this.src.substring(start, this.pos);
    if (tok.length === 0 || tok === '.') {
      throw new ParseError('formula.syntax', `Invalid number at position ${start}.`);
    }
    const n = Number(tok);
    if (!Number.isFinite(n)) {
      throw new ParseError('formula.syntax', `Invalid number '${tok}' at position ${start}.`);
    }
    return n;
  }

  private readIdentifier(): string {
    const start = this.pos;
    this.pos++;
    while (this.pos < this.src.length) {
      const c = this.src.charAt(this.pos);
      if (isIdentPart(c)) this.pos++;
      else break;
    }
    return this.src.substring(start, this.pos);
  }
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Parses the formula, enforcing resource limits and the whitelisted grammar.
 * Does NOT evaluate; returns the referenced identifier keys so the schema
 * validator can enforce field-exists / order rules.
 */
export function validateFormula(formula: string): FormulaValidationResult {
  if (formula == null || formula.trim().length === 0) {
    return {
      valid: false,
      errorCode: 'formula.syntax',
      errorMessage: 'Formula is empty.',
      referencedKeys: [],
    };
  }
  if (formula.length > MAX_FORMULA_LENGTH) {
    return {
      valid: false,
      errorCode: 'formula.too-long',
      errorMessage: `Formula exceeds ${MAX_FORMULA_LENGTH}-character limit.`,
      referencedKeys: [],
    };
  }

  const p = new Parser(formula);
  try {
    p.parseExpression(0);
    p.skipWhitespace();
    if (p.pos < p.src.length) {
      return {
        valid: false,
        errorCode: 'formula.syntax',
        errorMessage: `Unexpected token at position ${p.pos}: '${p.src.charAt(p.pos)}'.`,
        referencedKeys: [],
      };
    }
  } catch (e) {
    if (e instanceof ParseError) {
      return {
        valid: false,
        errorCode: e.code,
        errorMessage: e.message,
        referencedKeys: [],
      };
    }
    return {
      valid: false,
      errorCode: 'formula.syntax',
      errorMessage: 'Invalid formula.',
      referencedKeys: [],
    };
  }

  if (p.referencedKeys.length > MAX_IDENTIFIER_COUNT) {
    return {
      valid: false,
      errorCode: 'formula.too-many-identifiers',
      errorMessage: `Formula references more than ${MAX_IDENTIFIER_COUNT} identifiers.`,
      referencedKeys: [],
    };
  }

  return {
    valid: true,
    referencedKeys: p.referencedKeys.slice(),
  };
}

/**
 * Evaluates the formula against the given field values. Missing identifiers
 * resolve to zero; divide-by-zero and any arithmetic / parse error degrade
 * to zero. NEVER throws.
 */
export function evaluateFormula(
  formula: string,
  values: Record<string, number>,
): number {
  if (formula == null || formula.length === 0) return 0;
  if (formula.length > MAX_FORMULA_LENGTH) return 0;
  const vals = values == null ? {} : values;

  const ev = new Evaluator(formula, vals);
  try {
    const result = ev.parseExpression(0);
    ev.skipWhitespace();
    if (ev.pos < ev.src.length) return 0;
    if (ev.identifierCount > MAX_IDENTIFIER_COUNT) return 0;
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}
